/**
 * What differs between the concept's settings (parity tests) and the GDD's (production); master plan §1.
 */
import { config } from '../core/config.ts';
import { PERIOD } from '../core/math.ts';
import type { Fidelity, Mode } from './modes.ts';
import { conceptLattice, type Lattice } from './terrainSurface.ts';

/** Rows of a strip built along the river: how many, and where the strip ends. */
export interface StripRows {
  nz: number;
  z1: number;
}

export interface WorldSettings {
  /** Terrain grid: columns across (±320 m, packed toward the river) and rows along the strip. */
  terrain: StripRows & {
    nx: number;
    chunkLength: number | null;
    /** The concept grid the land's look is sampled from (decision D1-look); null draws the grid as is. */
    lattice: Lattice | null;
  };
  /** Water surface and bank foam rows. */
  water: StripRows;
  foam: StripRows;
  shadowHalfExtent: number;
  shadowMapSize: number;
  /** Water normal maps scroll upriver (concept) or downstream (GDD §3.4, D3). */
  waterScroll: 'upriver' | 'downstream';
  /** Foam around the island's shoreline too (GDD §3.4 "every shoreline", D2). */
  islandFoam: boolean;
  /** Vegetation split into z-bands with bounds so off-screen trees are culled (GDD §10.3). */
  vegetationBand: number | null;
  /**
   * Fixed draw order for the see-through layers built in world coordinates (foam, then particles) instead of
   * three's depth sort of their origin, which flips with the jet's distance from the world origin (judgment call 17).
   */
  stableLayers: boolean;
}

/** renderOrder of the see-through layers anchored at the world origin (see WorldSettings.stableLayers). */
export const LAYER = { foam: -2, particles: -1 } as const;

/** Terrain spacing required near the river (GDD §3.3, D1). */
export const NEAR_RIVER_SPACING = 1.4;
/** "Near the river": from the centerline out to 30 m past the bank, covering wet sand, beach and grass. */
export const NEAR_RIVER_BAND = 30;
/**
 * Rows no more than `maxSpacing` apart whose spacing divides 800 m, so every period of the strip is sampled at the
 * same z offsets and the floating-origin shift is seamless (GDD §3.3: the valley "repeat[s] seamlessly every 800 m").
 * The strip ends at the first row at or past z1.
 */
export function periodicRows(z0: number, z1: number, maxSpacing: number): StripRows {
  const spacing = PERIOD / Math.ceil(PERIOD / maxSpacing - 1e-9);
  const nz = Math.ceil((z0 - z1) / spacing - 1e-9);
  return { nz, z1: z0 - nz * spacing };
}

/** The concept's water and foam rows: 5 m and 3 m spread evenly over the strip (concept L720, L731). */
export const WATER_SPACING = 5;
export const FOAM_SPACING = 3;

/** Columns so that spacing across stays ≤ 1.4 m out to the widest bank (half-width 51 m) + 30 m: see tests. */
const GDD_NX = 360;

export function worldSettings(mode: Mode, fidelity: Fidelity, z0: number, z1: number): WorldSettings {
  if (fidelity === 'concept') {
    return {
      terrain: { nx: mode.res[0], nz: mode.res[1], z1, chunkLength: null, lattice: null },
      water: { nz: Math.round((z0 - z1) / WATER_SPACING), z1 },
      foam: { nz: Math.round((z0 - z1) / FOAM_SPACING), z1 },
      shadowHalfExtent: mode.shadow,
      shadowMapSize: config.render.shadowMapMin,
      waterScroll: 'upriver',
      islandFoam: false,
      vegetationBand: null,
      stableLayers: false,
    };
  }
  const extent = config.render.shadowHalfExtent;
  // D4: keep shadow texels per metre at least the concept's for this layout (2048 over its ±shadow extent).
  const conceptDensity = config.render.shadowMapMin / (2 * mode.shadow);
  const needed = Math.ceil((conceptDensity * 2 * extent) / 256) * 256;
  return {
    terrain: {
      nx: GDD_NX,
      ...periodicRows(z0, z1, NEAR_RIVER_SPACING),
      chunkLength: 100,
      lattice: conceptLattice(mode.res, z0, z1),
    },
    water: periodicRows(z0, z1, WATER_SPACING),
    foam: periodicRows(z0, z1, FOAM_SPACING),
    shadowHalfExtent: extent,
    shadowMapSize: Math.max(config.render.shadowMapMin, needed),
    waterScroll: 'downstream',
    islandFoam: true,
    vegetationBand: 100,
    stableLayers: true,
  };
}
